import { computed, inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';

import { BlockSegmentedNetsRepository } from '@data/repositories';
import type {
  TBlockSegmentedNetFilters,
  TBlockSegmentedNetParams,
  TBlockSegmentedNetSearch,
} from '@data/types';
import { Paginator } from '@domain/builders';
import { BlockSegmentedNet } from '@domain/entities';
import { Command } from '@domain/helpers';
import type { IPaginator } from '@domain/types';
import { NotifyService } from '@shared/services';
import type { TResultResponse } from '@shared/types';

type TListBlockResponse = {
  data: BlockSegmentedNet[];
  pagination: Paginator;
};

type TBlockSegmentedNetsState = {
  filters: TBlockSegmentedNetFilters;
  paginator: Paginator;
};

const initialState: TBlockSegmentedNetsState = {
  filters: {
    endDate: '',
    operationId: '',
    startDate: '',
  },
  paginator: Paginator.fromJson({}),
};

@Injectable({
  providedIn: 'root',
})
export class BlockSegmentedNetsProvider {
  readonly #state = signalState(initialState);
  readonly #blockSegmentedNetRep = inject(BlockSegmentedNetsRepository);
  readonly #notify = inject(NotifyService);

  readonly filters = this.#state.filters;
  readonly paginator = this.#state.paginator;

  readonly disableBlockNet = new Command<TResultResponse, string>(
    (id: string) => this.#blockSegmentedNetRep.disabledBlockSegmentedNet(id),
  );

  readonly listBlockNet = new Command<TListBlockResponse, void>(() =>
    this.listblockSegmentedNetsPaginated(),
  );

  readonly listBlockNetItems = computed(
    () => this.listBlockNet.data()?.data || [],
  );

  readonly createBlockNet = new Command<
    TResultResponse,
    TBlockSegmentedNetParams
  >((payload: TBlockSegmentedNetParams) =>
    this.#blockSegmentedNetRep.createNewBlockSegmentedNets(payload),
  );

  setFilters(filters: TBlockSegmentedNetFilters) {
    patchState(this.#state, () => ({
      filters,
      paginator: Paginator.fromJson({}),
    }));
  }

  setPaginator(pagination: Partial<IPaginator>) {
    patchState(this.#state, ({ paginator }) => ({
      paginator: paginator.copyWith(pagination),
    }));
  }

  hasFilters() {
    const { endDate, operationId, startDate } = this.filters();
    return !!endDate && !!operationId && !!startDate;
  }

  private async listblockSegmentedNetsPaginated() {
    const params: Partial<TBlockSegmentedNetSearch> = {
      ...(this.hasFilters() ? this.filters() : {}),
      page: this.paginator().page,
      limit: +this.paginator().pageSize,
    };

    const result =
      await this.#blockSegmentedNetRep.listblockSegmentedNetsPaginated(params);

    if (result.isOk === false) {
      this.#notify.errorByCode(result.error.code);
      return result;
    }

    const { data, pagination } = result.data;

    if (result.isOk && !data.length) {
      this.#notify.info('Información', 'No se encontraron registros');
      this.setPaginator(Paginator.fromJson({}));
      return result;
    }

    this.setPaginator(pagination);

    return result;
  }

  clear() {
    patchState(this.#state, initialState);
  }
}
